import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import LoginDocente from './pages/LoginDocente';
import MisClasesHoy from './pages/MisClasesHoy';
import MarcarAsistencia from './pages/MarcarAsistencia';
import FpsMeter from './components/FpsMeter';
import CargandoMarca from './components/CargandoMarca';
import ActualizacionGate from './components/ActualizacionGate';
import ErrorCarga from './components/ErrorCarga';
import { useActualizacionApp } from './hooks/useActualizacionApp';
import { importarConRecarga } from './utils/actualizacionApp';
import { useRefrescarNombreColegio } from './hooks/useNombreColegio';
import { esEntornoTest } from './utils/entorno';

// Rutas no críticas del flujo principal: code-split para que
// su JS (especialmente jsPDF en MiHorario) no entre al bundle inicial.
// Si el archivo de la pantalla ya no existe (deploy nuevo), la app se recarga sola.
const Perfil = lazy(() => importarConRecarga(() => import('./pages/Perfil')));
const MiHorario = lazy(() => importarConRecarga(() => import('./pages/MiHorario')));
const SelectorColegio = lazy(() => importarConRecarga(() => import('./pages/SelectorColegio')));

// Fallback mientras se descarga el chunk de una página lazy. Recibe el texto de
// cada pantalla para que coincida con su loader interno (sin saltos de texto).
const RouteFallback = ({ texto = '' }) => (
  <div className="flex items-center justify-center min-h-dvh bg-base-200">
    <CargandoMarca texto={texto} />
  </div>
);

const showFpsMeter = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('debug') === 'fps';

// Configurar StatusBar adaptable a dark/light mode
const setupNative = async () => {
  if (Capacitor.isNativePlatform()) {
    try {
      const { StatusBar, Style } = await import('@capacitor/status-bar');
      await StatusBar.setOverlaysWebView({ overlay: false });

      // Detectar tema y adaptar StatusBar
      const updateStatusBar = () => {
        const theme = document.documentElement.getAttribute('data-theme');
        const isDark = theme === 'dark' ||
          (!theme && window.matchMedia('(prefers-color-scheme: dark)').matches);
        StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light });
        StatusBar.setBackgroundColor({ color: isDark ? '#1d232a' : '#2563EB' });
      };

      updateStatusBar();

      // Observar cambios de tema
      const observer = new MutationObserver(updateStatusBar);
      observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', updateStatusBar);
    } catch (e) { /* no disponible en web */ }
  }
};

// Hook para manejar deep links (App Links)
const useDeepLinks = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let listener;
    import('@capacitor/app').then(({ App: CapApp }) => {
      listener = CapApp.addListener('appUrlOpen', ({ url }) => {
        try {
          const { pathname } = new URL(url);
          if (pathname && pathname !== '/') {
            navigate(pathname, { replace: true });
          }
        } catch (e) { /* url inválida */ }
      });
    });

    return () => { listener?.then(l => l.remove()); };
  }, [navigate]);
};

// Hook para manejar el botón back de Android
const useAndroidBackButton = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let cleanup;
    import('@capacitor/app').then(({ App: CapApp }) => {
      const listener = CapApp.addListener('backButton', ({ canGoBack }) => {
        if (location.pathname === '/mis-clases' || location.pathname === '/login') {
          CapApp.exitApp();
        } else if (location.pathname === '/horario' || location.pathname === '/perfil') {
          navigate('/mis-clases');
        } else if (canGoBack) {
          navigate(-1);
        } else {
          navigate('/mis-clases');
        }
      });
      cleanup = () => listener.then(l => l.remove());
    });

    return () => { if (cleanup) cleanup(); };
  }, [navigate, location.pathname]);
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('docente_token');

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Inner component that uses router hooks
const AppRoutes = () => {
  const location = useLocation();
  useRefrescarNombreColegio(location.pathname);
  useActualizacionApp();
  useAndroidBackButton();
  useDeepLinks();
  useEffect(() => { setupNative(); }, []);

  return (
    <ErrorCarga>
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginDocente />} />

        {/* Protected Routes */}
        <Route
          path="/mis-clases"
          element={
            <ProtectedRoute>
              <MisClasesHoy />
            </ProtectedRoute>
          }
        />
        <Route
          path="/marcar-asistencia"
          element={
            <ProtectedRoute>
              <MarcarAsistencia />
            </ProtectedRoute>
          }
        />
        <Route
          path="/perfil"
          element={
            <ProtectedRoute>
              <Suspense fallback={<RouteFallback texto="Cargando perfil…" />}>
                <Perfil />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/horario"
          element={
            <ProtectedRoute>
              <Suspense fallback={<RouteFallback texto="Cargando horario…" />}>
                <MiHorario />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/selector-colegio"
          element={
            <ProtectedRoute>
              <Suspense fallback={<RouteFallback texto="Cargando…" />}>
                <SelectorColegio />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/mis-clases" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>
    </ErrorCarga>
  );
};

function App() {
  return (
    <Router>
      {esEntornoTest && <aside role="status" data-entorno="test" className="bg-amber-100 text-amber-950 border-b border-amber-200 px-3 py-1 text-center text-xs font-semibold">Ambiente TEST · datos de prueba</aside>}
      {!esEntornoTest && <ActualizacionGate />}
      <AppRoutes />
      {showFpsMeter && <FpsMeter />}
    </Router>
  );
}

export default App;
